import type { PaymentProviderName, Plan, Product } from "@cross-border-agent/contracts";

const paymentChannels = [
  { code: "wechat", name: "微信支付", description: "微信扫码付款" },
  { code: "alipay", name: "支付宝支付", description: "前往支付宝收银台" },
] as const;

const money = (cents: number) => new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" }).format(cents / 100);

export function preferredPaymentProvider(providers: PaymentProviderName[]): PaymentProviderName | null {
  return (["wechat", "alipay", "mock"] as const).find(provider => providers.includes(provider)) ?? null;
}

export function PaymentMethods({ providers, selected, onSelect, locked = false }: {
  providers: PaymentProviderName[];
  selected: PaymentProviderName | null;
  onSelect: (provider: PaymentProviderName) => void;
  locked?: boolean;
}) {
  return <fieldset className="payment-methods"><legend>支付方式</legend>
    {paymentChannels.map(channel => {
      const available = providers.includes(channel.code);
      return <label key={channel.code} className={available ? "" : "is-unavailable"}>
        <input type="radio" name="payment-provider" value={channel.code} checked={selected === channel.code && available} disabled={!available || locked} onChange={() => onSelect(channel.code)} />
        <span><strong>{channel.name}</strong><small>{available ? channel.description : "待接入 · 商户参数尚未配置"}</small></span>
      </label>;
    })}
    {providers.includes("mock") && <label>
      <input type="radio" name="payment-provider" value="mock" checked={selected === "mock"} disabled={locked} onChange={() => onSelect("mock")} />
      <span><strong>本地模拟支付</strong><small>仅验证订单与权益流程，不会真实扣款</small></span>
    </label>}
    {providers.length === 0 && <p className="field-hint">暂无可用支付渠道，暂时不能下单。</p>}
  </fieldset>;
}

export function PaymentAvailability({ providers }: { providers: PaymentProviderName[] }) {
  const hasRealPayment = paymentChannels.some(channel => providers.includes(channel.code));
  return <section className="payment-availability" aria-label="支付渠道状态">
    <div className="payment-channel-status">{paymentChannels.map(channel => <span key={channel.code}>
      <strong>{channel.name}</strong><small>{providers.includes(channel.code) ? "已配置" : "待接入"}</small>
    </span>)}</div>
    {!hasRealPayment && <p>{providers.includes("mock") ? "微信、支付宝商户参数尚未配置。目前仅支持本地模拟支付，不会发起真实扣款。" : "支付渠道尚未接通，暂不支持购买。"}</p>}
  </section>;
}

function PlanBenefits({ plan }: { plan: Plan }) {
  return <ul><li>有效期 {plan.durationDays} 天</li><li>每天赠送 {plan.dailyGrantPoints} 积分</li><li>当日积分当日有效</li></ul>;
}

export function CommerceCatalog({ plans, products, supportBlocked, onSelect }: {
  plans: Plan[];
  products: Product[];
  supportBlocked: boolean;
  onSelect: (product: Product) => void;
}) {
  const activePlans = plans.filter(plan => plan.active);
  const activeProducts = products.filter(product => product.active);
  // Existing local seed products are separate from the public pricing catalog.
  const demoProducts = activeProducts.filter(product => product.code.startsWith("local_"));
  const publicProducts = activeProducts.filter(product => !product.code.startsWith("local_"));
  const planCodes = new Set(activePlans.map(plan => plan.code));
  const otherProducts = publicProducts.filter(product => product.kind !== "ai_plan" || !product.planCode || !planCodes.has(product.planCode));
  const renderProduct = (product: Product, plan?: Plan) => {
    const blocked = product.kind === "human_support" && supportBlocked;
    return <article key={product.id}>
      <span className="product-kind">{product.kind === "ai_plan" ? "AI 套餐" : product.kind === "point_pack" ? "积分包" : "真人答疑"}</span>
      <h2>{product.name}</h2><p>{product.description}</p><strong className="price">{money(product.priceCents)}</strong>
      {plan && <PlanBenefits plan={plan} />}
      {product.kind === "point_pack" && <ul><li>到账 {product.grantPoints} 积分</li><li>与套餐积分统一显示</li></ul>}
      <button className="primary" disabled={blocked} onClick={() => onSelect(product)}>{blocked ? "请重新整理问题" : product.kind === "human_support" ? "查看辅导服务" : "购买"}</button>
    </article>;
  };
  return <>
    {activePlans.length > 0 || publicProducts.length > 0 ? <section className="product-grid" aria-label="套餐与服务列表">
      {activePlans.flatMap(plan => {
        const offers = publicProducts.filter(product => product.kind === "ai_plan" && product.planCode === plan.code);
        return offers.length > 0 ? offers.map(product => renderProduct(product, plan)) : [
          <article key={`plan-${plan.code}`} className="plan-preview">
            <span className="product-kind">AI 套餐 · 待上架</span><h2>{plan.name}</h2><p>{plan.description}</p>
            <strong className="price pending-price">价格待公布</strong><PlanBenefits plan={plan} />
            <button className="primary" disabled>待上架</button>
          </article>,
        ];
      })}
      {otherProducts.map(product => renderProduct(product))}
    </section> : <section className="empty"><h2>商品尚未上架</h2><p>套餐价格和服务内容确认后将在这里展示。</p></section>}
    {demoProducts.length > 0 && <details className="local-payment-demo">
      <summary>本地支付联调（非正式套餐）</summary>
      <p>下列商品仅用于验证流程，其金额不是正式套餐售价。</p>
      {demoProducts.map(product => <div key={product.id}>
        <span><strong>{product.name}</strong><small>{product.description}</small></span>
        <b>{money(product.priceCents)}</b><button type="button" onClick={() => onSelect(product)}>测试购买</button>
      </div>)}
    </details>}
  </>;
}
